import React, { useState, useEffect } from 'react';
import {
    FaUsers, FaTrophy, FaMoneyBillWave, FaChartLine,
    FaExclamationCircle, FaFileDownload, FaSync, FaServer
} from 'react-icons/fa';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import Chart from 'react-apexcharts';

const DashboardAdmin = () => {
    const [stats, setStats] = useState({
        totalTeams: 0,
        totalAthletes: 0,
        pendingPayments: 0,
        totalRevenue: 0
    });
    const [chartData, setChartData] = useState({
        options: {
            chart: { id: 'kategori-chart', fontFamily: 'inherit' },
            xaxis: { categories: [] }
        },
        series: [{ name: 'Jumlah Atlet', data: [] }]
    });
    const [eventName, setEventName] = useState("Memuat...");
    const [loading, setLoading] = useState(true);

    // --- FETCH DASHBOARD DATA ---
    const fetchDashboardData = async () => {
        setLoading(true);
        try {
            const response = await api.get('/dashboard/admin');
            const { stats, chartData, eventName } = response.data;

            setStats(stats);
            setEventName(eventName);

            // Update Chart (Desain Modern)
            setChartData({
                options: {
                    chart: {
                        id: 'kategori-chart',
                        toolbar: { show: false },
                        fontFamily: 'inherit',
                        animations: { enabled: true, easing: 'easeinout', speed: 800 }
                    },
                    plotOptions: {
                        bar: { borderRadius: 6, columnWidth: '40%' }
                    },
                    dataLabels: { enabled: false },
                    stroke: { show: true, width: 2, colors: ['transparent'] },
                    xaxis: {
                        categories: chartData.categories,
                        axisBorder: { show: false },
                        axisTicks: { show: false }
                    },
                    grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
                    colors: ['#4f46e5'], // Warna Indigo khas SaaS
                    fill: { opacity: 1 }
                },
                series: [{ name: 'Jumlah Atlet', data: chartData.series }]
            });

        } catch (error) {
            console.error("Gagal memuat dashboard:", error);
            toast.error("Gagal memuat data statistik.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, []);

    // --- DOWNLOAD REKAP CSV ---
    const handleDownloadReport = async () => {
        try {
            // Sesuaikan dengan rute yang terdaftar di AthleteRoute.js
            const response = await api.get('/athletes/export/csv', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Rekap_Atlet_${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success("Laporan berhasil didownload!");
        } catch (error) {
            toast.error("Gagal download laporan.");
        }
    };

    // Helper Format Rupiah
    const formatRupiah = (num) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
                <div className="spinner-border text-primary" role="status"></div>
            </div>
        );
    }

    return (
        <div className="container-fluid p-0 animate-fade-in">
            {/* HEADER */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                <div>
                    <h4 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                        <FaChartLine className="text-primary" /> Ringkasan Sistem
                    </h4>
                    <p className="text-muted small mb-0">Statistik real-time untuk <b>{eventName}</b></p>
                </div>
                <div className="d-flex gap-2">
                    <button onClick={fetchDashboardData} className="btn btn-white border shadow-sm rounded-circle d-flex align-items-center justify-content-center hover-scale text-primary" style={{ width: '42px', height: '42px' }} title="Refresh Data">
                        <FaSync />
                    </button>
                    <button onClick={handleDownloadReport} className="btn btn-primary rounded-pill px-4 py-2 shadow-sm fw-bold d-flex align-items-center gap-2 hover-scale" style={{ fontSize: '0.9rem' }}>
                        <FaFileDownload /> Unduh Rekap CSV
                    </button>
                </div>
            </div>

            {/* STATS CARDS (GRID 4 KOLOM) */}
            <div className="row g-3 mb-4">
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="card border-0 shadow-sm rounded-4 h-100 bg-white hover-up">
                        <div className="card-body p-4 d-flex align-items-center justify-content-between">
                            <div>
                                <p className="text-muted small fw-bold mb-1 text-uppercase" style={{ letterSpacing: '0.5px' }}>Total Atlet</p>
                                <h3 className="fw-black text-dark m-0">{stats.totalAthletes}</h3>
                            </div>
                            <div className="bg-primary bg-opacity-25 text-primary rounded-4 d-flex align-items-center justify-content-center" style={{ width: '54px', height: '54px' }}>
                                <FaUsers size={24} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="card border-0 shadow-sm rounded-4 h-100 bg-white hover-up">
                        <div className="card-body p-4 d-flex align-items-center justify-content-between">
                            <div>
                                <p className="text-muted small fw-bold mb-1 text-uppercase" style={{ letterSpacing: '0.5px' }}>Total Pemasukan</p>
                                <h4 className="fw-black text-success m-0">{formatRupiah(stats.totalRevenue)}</h4>
                            </div>
                            <div className="bg-success bg-opacity-25text-success rounded-4 d-flex align-items-center justify-content-center" style={{ width: '54px', height: '54px' }}>
                                <FaMoneyBillWave size={24} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="card border-0 shadow-sm rounded-4 h-100 bg-white hover-up">
                        <div className="card-body p-4 d-flex align-items-center justify-content-between">
                            <div>
                                <p className="text-muted small fw-bold mb-1 text-uppercase" style={{ letterSpacing: '0.5px' }}>Tim Terdaftar</p>
                                <h3 className="fw-black text-warning m-0">{stats.totalTeams}</h3>
                            </div>
                            <div className="bg-warning bg-opacity-25 text-warning rounded-4 d-flex align-items-center justify-content-center" style={{ width: '54px', height: '54px' }}>
                                <FaTrophy size={24} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-sm-6 col-xl-3">
                    <div className="card border-0 shadow-sm rounded-4 h-100 bg-white hover-up">
                        <div className="card-body p-4 d-flex align-items-center justify-content-between">
                            <div>
                                <p className="text-muted small fw-bold mb-1 text-uppercase" style={{ letterSpacing: '0.5px' }}>Verifikasi Tertunda</p>
                                <h3 className="fw-black text-danger m-0">{stats.pendingPayments}</h3>
                            </div>
                            <div className="bg-danger bg-opacity-25 text-danger rounded-4 d-flex align-items-center justify-content-center" style={{ width: '54px', height: '54px' }}>
                                <FaExclamationCircle size={24} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CHART & INFO PANEL */}
            <div className="row g-3">
                {/* CHART */}
                <div className="col-lg-8">
                    <div className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden">
                        <div className="card-header bg-white border-bottom-0 pt-4 pb-0 px-4">
                            <h6 className="fw-bold text-dark m-0">Grafik Kategori Pertandingan</h6>
                        </div>
                        <div className="card-body px-4 pb-4 pt-2">
                            <Chart options={chartData.options} series={chartData.series} type="bar" height={320} />
                        </div>
                    </div>
                </div>

                {/* INFO PANEL */}
                <div className="col-lg-4">
                    <div className="card border-0 shadow-sm rounded-4 h-100 bg-primary text-white position-relative overflow-hidden hover-up">
                        <div className="position-absolute end-0 top-0 opacity-25 p-4" style={{ transform: 'rotate(15deg) scale(1.2)' }}>
                            <FaServer size={120} />
                        </div>
                        <div className="card-body p-4 p-md-5 position-relative z-1 d-flex flex-column justify-content-center">
                            <h5 className="fw-bold mb-4 d-flex align-items-center gap-2">
                                <div className="spinner-grow spinner-grow-sm text-success" role="status"></div>
                                System Status
                            </h5>

                            <ul className="list-unstyled d-flex flex-column gap-3 mb-4 opacity-100">
                                <li className="d-flex justify-content-between align-items-center bg-white bg-opacity-25 p-3 rounded-3">
                                    <span className="small fw-medium">Server Connection</span>
                                    <span className="badge bg-success rounded-pill px-3">Online</span>
                                </li>
                                <li className="d-flex justify-content-between align-items-center bg-white bg-opacity-25 p-3 rounded-3">
                                    <span className="small fw-medium">Database Node</span>
                                    <span className="badge bg-success rounded-pill px-3">Stable</span>
                                </li>
                                <li className="d-flex justify-content-between align-items-center bg-white bg-opacity-25 p-3 rounded-3">
                                    <span className="small fw-medium">Pendaftaran Atlet</span>
                                    <span className="badge bg-info rounded-pill px-3 text-dark fw-bold">Dibuka</span>
                                </li>
                            </ul>

                            <div className="mt-auto pt-3 border-top border-white border-opacity-25">
                                <p className="small text-white-50 mb-0" style={{ lineHeight: '1.6' }}>
                                    Sistem terpantau stabil. Pastikan untuk meninjau menu verifikasi pembayaran secara berkala guna melancarkan proses administrasi kontingen.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CSS ANIMATION & STYLE */}
            <style>{`
                .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
                .hover-scale { transition: transform 0.2s; }
                .hover-scale:hover { transform: scale(1.05); }
                .hover-up { transition: transform 0.2s, box-shadow 0.2s; }
                .hover-up:hover { transform: translateY(-4px); box-shadow: 0 .5rem 1rem rgba(0,0,0,.08)!important; }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default DashboardAdmin;